/*
 * Copyright (C) 2016 - 2024 Vivien Pelenc / CEFE / CNRS
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */


import React, {useEffect, useRef, useState} from 'react';
import Chart from 'chart.js/auto';

import {
    getCumul,
    getCumulAnnuel,
    getCumulJournalier,
    getCumulMensuel,
    getDonneHoraireDate,
    getDonneJournalierDate,
    getDonneMensuelleDate
} from "../ConnectionBDD";

export default function ChartCumulDynamique() {
    const unite = 'mm';
    const [cumulData, setCumulData] = useState([]);
    const [labels, setLabels] = useState([]);
    const [depth, setDepth] = useState(0);
    const [year, setYear] = useState(new Date().getFullYear());
    const [month, setMonth] = useState(new Date().getMonth() + 1);
    const [day, setDay] = useState(new Date().getDay());

    useEffect(() => {
        const fetchData = async () => {
            try {
                //console.log("depth : "+depth);
                let response;
                switch(depth) {
                    case 0:

                        response = await getCumulAnnuel();


                        setCumulData(Object.values(response).map(value => value.cumulativeRainYear));
                        setLabels(Object.values(response).map(value => value.year));
                        //console.log(Object.values(response).map(value => value.cumulativeRainYear));
                        break;
                    case 1:
                        //console.log("01/01/" + year+ " 31/12/" + year);
                        response = await getDonneMensuelleDate('01/01/'+year, '31/12/'+year);


                        //console.log("2 : "+response);

                        setCumulData(Object.values(response).map(value => value.cumulativeRainMonth));
                        setLabels(Object.values(response).map(value => `${value.month}/${value.year}`));

                        break;
                    case 2:

                        response = await getDonneJournalierDate('01/'+month+'/'+year, '31/'+month+'/'+year);
                        //console.log("3 : "+response);

                        setCumulData(Object.values(response).map(value => value.cumulativeRainDay));
                        setLabels(Object.values(response).map(value => new Date(value.date).getDate()+'/'+(new Date(value.date).getMonth()+1)+'/'+new Date(value.date).getFullYear()));
                        break;
                    case 3:

                        response = await getDonneHoraireDate(day + '/' + month + '/' + year, day + '/' + month + '/' + year);




// Fonction de comparaison pour trier par heure
                    function compareByTime(a, b) {
                        const timeA = new Date(`1970-01-01T${a.time}Z`); // Supposons que le temps soit au format HH:MM
                        const timeB = new Date(`1970-01-01T${b.time}Z`);
                        return timeA - timeB;
                    }

// Trier les données par heure
                        const sortedResponse = Object.values(response).sort(compareByTime);

// Mettre à jour les données cumulatives et les étiquettes avec les données triées
                        setCumulData(sortedResponse.map(value => value.cumulativeRain));
                        setLabels(sortedResponse.map(value => `${value.time}`));

                        //console.log(sortedResponse);

                        break;
                    default:
                        break;
                }
            } catch (error) {
                console.error('Erreur lors de la récupération des données de cumul:', error);
            }
        };

        fetchData();

        const refreshData = setInterval(() => {
            const currentDate = new Date();
            const minutes = currentDate.getMinutes();
            if (minutes === 1 || minutes === 31) {
                fetchData();
            }
        }, 59000);

        return () => clearInterval(refreshData);
    }, [depth]);

    return (
        <div>
            <Graph labels={labels} data={cumulData} unite={unite} depth={depth} setDepth={setDepth} setYear={setYear} setMonth={setMonth} setDay={setDay}/>
        </div>
    );
}

function Graph({ labels, data, unite, depth, setDepth, setYear, setMonth, setDay}) {
    const canvasRef = useRef(null);
    const chartRef = useRef(null);

    //Moyenne de data
    var moyenne = 0;
    for (var i = 0; i < data.length; i++) {
        moyenne += data[i];
    }
    moyenne = moyenne / data.length;
    moyenne = Number(moyenne.toFixed(1));
    data = data.map(value => Number(value.toFixed(1)));


    var moyenneData = new Array(data.length).fill(moyenne);






    useEffect(() => {
        if (canvasRef.current) {
            if (chartRef.current) {
                chartRef.current.destroy();
            }
            const ctx = canvasRef.current.getContext('2d');
            chartRef.current = new Chart(ctx, {
                type: 'bar',
                data: {
                    labels: labels,
                    datasets: [{
                        label: unite,
                        data: data,
                        backgroundColor: 'rgb(59,96,227)',
                    },
                        {
                            label: 'Moyenne',
                            data: moyenneData,
                            type: 'line', // Le type spécifique de ce dataset est 'line'
                            borderColor: 'rgb(102,209,255)',
                            backgroundColor: 'rgb(102,209,255)',
                            pointStyle: false,
                            fill: false
                        }]
                },
                options: {

                    interaction: {
                        mode: 'index',
                        intersect: false
                    },

                    scales: {
                        y: {
                            beginAtZero: true,
                            ticks: {
                                callback: function(value) {
                                    return value + ' mm'; // Ajoute un pourcentage après chaque valeur
                                }
                            }
                        }
                    },
                    plugins: {
                        title: {
                            display: true,
                            text: `Cumul Précipitations ${depth === 0 ? 'Annuel' : depth === 1 ? 'Mensuel' : depth === 2 ? 'Journalier' : 'Horaire'} (en mm)`,
                        },
                        legend: {
                            display: false
                        },
                        tooltip: {
                            mode: 'index',
                            intersect: false
                        }
                    },
                    onClick: (e, activeEls) => {
                        if (activeEls.length > 0) {
                            setDepth(prevDepth => (prevDepth < 3 ? prevDepth + 1 : 3));
                            if (depth === 0) {
                                setYear(labels[activeEls[0].index]);
                            }
                            if (depth === 1) {
                                var m = labels[activeEls[0].index].split('/')[0]
                                setMonth(m);
                            }
                            if (depth === 2) {
                                var d = labels[activeEls[0].index].split('/')[0]
                                setDay(d);
                            }
                        }
                    }
                }
            });
        }
    }, [labels, data, depth, setDepth, setYear, setMonth, setDay]);

    if (depth === 0) {
        return <div>
            <canvas id="graphique" ref={canvasRef} width="100" height="100"></canvas>
        </div>;
    }
    return <div>
           <canvas id="graphique" ref={canvasRef} width="100" height="100"></canvas>
            {depth !== 0 && <button onClick={() => setDepth(0)}>Réinitialiser</button>}
            {depth !== 0 && <button onClick={() => setDepth(depth-1)}>Retour</button>}
           </div>;
}